/*  dead but tested file: Recognized as dead file, but used by go/src/atlas/encrypted_folder_download/service_worker/src/service_worker.ts */

import type {SeverityLevel} from '@sentry/browser';
import {captureException, init as initSentry, setContext, withScope} from '@sentry/browser';
import {isNil, isEmpty} from 'lodash-es';

type Primitive = number | string | boolean | bigint | symbol | null | undefined;

export type SentryErrorExtras = {
  extras?: {[key: string]: Primitive};
  tags?: {[key: string]: Primitive};

  severity?: SeverityLevel;
};

export class SentryError extends Error {
  extras: {[key: string]: Primitive} = {};
  tags: {[key: string]: Primitive} = {};
  severity: SeverityLevel = 'fatal';
  reported: boolean = false;

  constructor(e: unknown, extras?: SentryErrorExtras) {
    if (e instanceof SentryError) {
      // Reuse the passed SentryError
      e.tags = {...extras?.tags, ...e.tags};
      e.extras = {...extras?.extras, ...e.extras};
      return e;
    }

    super(`${e}`);

    if (e instanceof Error) {
      this.name = e.name;
      this.message = e.message;
      this.stack = e.stack;
    }

    if (isNil(this.stack) || isEmpty(this.stack)) {
      // We don't have a stack trace yet (e.g. because `e` is just
      // a string), and don't know if this error will be thrown in
      // the future (or just logged). Thus, throw one right here to
      // construct a stack trace, just to be sure.
      try {
        throw new Error(`${e}`);
      } catch (e) {
        this.stack = (e as Error).stack;
      }
    }

    this.extras = extras?.extras || {};
    this.tags = extras?.tags || {};
    this.severity = extras?.severity || 'fatal';
    this.reported = false;
  }
}

let SENTRY_INITIALIZED = false;

export function setGlobalContext(path: string, downloadUrl: string | null) {
  if (!SENTRY_INITIALIZED) {
    return;
  }

  setContext('request', {
    path,
    downloadUrl,
  });
}

export function captureError(e: SentryError) {
  if (!SENTRY_INITIALIZED || e.reported) {
    return;
  }

  withScope((scope) => {
    scope.setLevel(e.severity);
    scope.setTags(e.tags);
    scope.setContext('Extras', e.extras);

    captureException(e);

    e.reported = true;
  });
}

export function captureErrorEvent(e: ErrorEvent) {
  if (!SENTRY_INITIALIZED) {
    return;
  }

  const extras = {
    tags: {handled: false, onerror: true},
    extras: {unhandledError: true, filename: e.filename, lineno: e.lineno, colno: e.colno},
  };

  // Synthesize a Sentry error
  const error =
    e.error instanceof Error
      ? new SentryError(e.error, extras)
      : new SentryError(e.message, extras);

  captureError(error);
}

export function capturePromiseRejection(e: PromiseRejectionEvent) {
  if (!SENTRY_INITIALIZED) {
    return;
  }

  const error = new SentryError(e.reason, {
    tags: {handled: false, onunhandledrejection: true},
    extras: {unhandledPromiseRejection: true},
  });

  captureError(error);
}

/**
 * Initialize the Sentry plugin for reporting to 'encrypted-folder-web'
 * only in production mode.
 */
export function init(useDefaultIntegrations?: boolean) {
  if (process.env.NODE_ENV === 'production') {
    const dsn = 'https://d9d5d2838ecf2dc7eeb1da329a4b44e9@d.dropbox.com/4506336666386432';

    // defaultIntegrations has type `false | Integration[]`...
    if (useDefaultIntegrations) {
      initSentry({dsn});
    } else {
      initSentry({dsn, defaultIntegrations: false});
    }

    SENTRY_INITIALIZED = true;
  }
}
